import { Injectable } from '@angular/core';
import { Actions,createEffect,ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, exhaustMap, mergeMap, of } from 'rxjs';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { catchError,first,map,switchMap,tap,withLatestFrom } from 'rxjs/operators';
import { calculateConsolidatedAmount } from '../../../dave-reports/components/arl-calculation/arl-calculation.helper';
import {
isNotNullOrUndefined,
stringifyIfNotNullOrUndefined,
stringifyIfNotUndefined
} from '../../../helper/helper';
import * as ibbau from '../../../helper/ibbau.helper';
import * as tsb from '../../../helper/tsb.helper';
import {
    LedgerImportAdditionalData, LedgerImportDocumentTypes, LedgerImportEntity,
    LedgerImportEntityFromBackend,
    LedgerImportStatusEnum,
    LedgerImportStatusNames,
} from '../../entities/ledger-import.entity';
import { PartnerTypeEnum } from '../../entities/partner.entity';
import { SfdtTemplateEntity,SfdtTemplateEntityFromBackend } from '../../entities/sfdt-template.entity';
import {
    DaveMutationChangeLedgerImportArgs, DaveMutationCreateClosingOrPartialInvoiceFromLedgerImportArgs,
} from '../../graphql-types';
import { BusinessVolumeResolver } from '../../guards/business-volume.resolver';
import { CommissionResolver } from '../../guards/commission.resolver';
import { CustomerResolver } from '../../guards/customer.resolver';
import { EmployeeResolver } from '../../guards/employee.resolver';
import { OfficeResolver } from '../../guards/office.resolver';
import { PartnerOfficeResolver } from '../../guards/partner-office.resolver';
import { SFDTTemplateResolver } from '../../guards/sfdt-template.resolver';
import { SurchargeRateResolver } from '../../guards/surcharge-rate.resolver';
import {
    BackendDate,
    FrontendDate,
    useGzip,
} from '../../helper/backend-frontend-conversion.helper';
import { getFetched$ } from '../../helper/helper';
import { ArlDataService,arlPostQuery } from '../../services/arl-data.service';
import { FileDataService } from '../../services/file-data.service';
import { HttpService } from '../../services/http.service';
import { LedgerImportActionTypes } from '../actions/accounting.actions';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { FilesActionTypes } from '../actions/files.actions';
import { State } from '../index';
import {
getSfdtTemplates,
getSfdtTemplatesFetched
} from '../selectors/accounting.selector';
import { getToken } from '../selectors/base.selectors';
import { getBusinessVolumeFetched } from '../selectors/business-volume.selector';
import { getCommissionDictionary,getCommissionsFetched } from '../selectors/commission.selector';
import { getCustomers,getCustomersFetched } from '../selectors/customers.selectors';
import { getEmployees,getEmployeesFetched } from '../selectors/employees.selectors';
import { getLedgerImports } from '../selectors/ledger-import.selector';
import { getOffices,getOfficesFetched } from '../selectors/offices.selectors';
import { getOwnPartnerOffices,getPartner,getPartnerOfficesFetched } from '../selectors/partners.selectors';
import { arlTypeForSurchargeRate,getSurchargeRate,getSurchargeRateFetched } from '../selectors/surcharge-rate.selector';
import { getUser } from '../selectors/users.selectors';
import { financialRound } from '../../entities/accounts-receivable-ledger.entity';
import { LedgerImportDataService } from '../../services/ledger-import-data.service';
import { GetTimestampFromTime } from '@dave/types';

export enum LedgerImportEffectErrorCodes {
    Load = 'Rechnung Abrufen fehlgeschlagen',
    Remove = 'Rechnung Löschen fehlgeschlagen',
    Modify = 'Rechnung ändern fehlgeschlagen',
    Duplicate = 'Rechnung duplizieren fehlgeschlagen',
    Add = 'Rechnung hinzufügen fehlgeschlagen',
    CreateXRechnungFromLedgerImport = 'x-Rechnung generieren fehlgeschlagen',
    sfdtTemplateGet = 'Rechnungstemplates abrufen fehlgeschlagen',
    CreatePartialInvoice = 'Abschlagsrechnung erstellen fehlgeschlagen',
    CreateCumulativePartialInvoice = 'Kumulative Abschlagsrechnung erstellen fehlgeschlagen',
    CreateClosingInvoice = 'Schlussrechnung erstellen fehlgeschlagen',
}

const stringifyPaymentItem = (Payload:  DaveMutationChangeLedgerImportArgs['paymentItems'][0]) => {
    return `{
    ${Payload.bVType !== undefined ? 'bVType: ' + Payload.bVType : ''}
    ${stringifyIfNotUndefined(Payload, 'businessVolumeId')}
    ${stringifyIfNotUndefined(Payload, 'businessVolumeNo')}
    ${stringifyIfNotUndefined(Payload, 'payedAmountBrutto')}
    ${stringifyIfNotUndefined(Payload, 'payedAmountNetto')}
    ${stringifyIfNotUndefined(Payload, 'paymentDate')}
    }`
}
const stringifyPaymentItems = (paymentItems:  DaveMutationChangeLedgerImportArgs['paymentItems']) => {
    return `paymentItems: [
    ${paymentItems.map(stringifyPaymentItem).join(',\n')}
    ]`;
}
const stringifylVKalkulationNebenkostenItem = (Payload:  DaveMutationChangeLedgerImportArgs['lVKalkulationNebenkosten'][0]) => {
    return `{
    ${stringifyIfNotUndefined(Payload, 'name')}
    ${stringifyIfNotUndefined(Payload, 'gewerke')}
    ${stringifyIfNotUndefined(Payload, 'comment')}
    }`
}
const stringifylVKalkulationNebenkosten = (lVKalkulationNebenkosten:  DaveMutationChangeLedgerImportArgs['lVKalkulationNebenkosten']) => {
    return `lVKalkulationNebenkosten: [
    ${lVKalkulationNebenkosten.map(stringifylVKalkulationNebenkostenItem).join(',\n')}
    ]`;
}

const stringifyArlsItem = (arl: { id: number; baseCost: number; lohnBaseCostPart: number; costAmount: number; baseCostGross: number }) => {
    return `{ id: ${arl.id}, baseCost: ${arl.baseCost}, lohnBaseCostPart: ${arl.lohnBaseCostPart}, costAmount: ${arl.costAmount}, baseCostGross: ${arl.baseCostGross} }`;
};


const stringifyArls = (
    arls: DaveMutationCreateClosingOrPartialInvoiceFromLedgerImportArgs['arls']) => {
    return `arls: [
    ${arls.map(stringifyArlsItem).join(',\n')}
    ]`;
}

@Injectable()
export class LedgerImportEffects {
    constructor(
        private actions$: Actions<DaveActions>,
        private store$: Store<State>,
        private gatewayHttpService: HttpService,
        private apiToasterService: ToastrService,
        private bvResolver: BusinessVolumeResolver,
        private fileDataService: FileDataService,
        private customerResolver: CustomerResolver,
        private officeResolver: OfficeResolver,
        private partnerOfficeResolver: PartnerOfficeResolver,
        private sFDTTemplateResolver: SFDTTemplateResolver,
        private commissionResolver: CommissionResolver,
        private surchargeRateResolver: SurchargeRateResolver,
        private arlDataService: ArlDataService,
        private employeeResolver: EmployeeResolver,
        private ledgerImportDataService: LedgerImportDataService,
    ) {}

    Load = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.GetLedgerImports),
            exhaustMap(({ Payload }, ) => {
                return this.ledgerImportDataService.get$({
                    LedgerImportIds: [],
                    CommissionIds: [],
                    CustomerIds: [],
                    EventIds: [],
                    UserIds: [],
                    DocumentTypes: [],
                    DocumentStatus: [],
                    UpdatedSince: Payload?.updatedSince ? GetTimestampFromTime(BackendDate(Payload.updatedSince)).toString() : undefined,
                    WithDeleted: !!Payload?.updatedSince,
                }).pipe(
                    mergeMap((res) => {
                        if (!res) {
                            return of(
                                LedgerImportActionTypes.UpdateMany({
                                    Payload: [],
                                    updateLatestUpdatedAt: false,
                                }),
                            );
                        }

                        // const mapped = res.ledgerImport.map((val) =>
                        //     LedgerImportEntityFromBackend(val),
                        // );

                        if (res && Payload?.updatedSince) {
                            return of(
                                LedgerImportActionTypes.UpdateMany({
                                    Payload: res.filter((val) => !val.DeletedAt),
                                    updateLatestUpdatedAt: true,
                                }),
                                LedgerImportActionTypes.RemoveMany({
                                    Payload: res
                                        .filter((val) => !!val.DeletedAt)
                                        .map((val) => val.Id),
                                }),
                            );
                        }

                        return of(
                            LedgerImportActionTypes.UpdateAll({
                                Payload: res,
                                updateLatestUpdatedAt: true,
                            }),
                        );
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.Load,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    AddLedgerImportByDefaultsQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.AddLedgerImportWithDefaults),
            withLatestFrom(this.store$.select(getUser), this.store$.select(getPartner)),
            concatMap((v) =>
                combineLatest([
                    of(v),
                    getFetched$(this.store$, getCustomersFetched, getCustomers, this.customerResolver),
                    getFetched$(this.store$, getOfficesFetched, getOffices, this.officeResolver),
                    getFetched$(this.store$, getPartnerOfficesFetched, getOwnPartnerOffices, this.partnerOfficeResolver),
                    getFetched$(this.store$, getSfdtTemplatesFetched, getSfdtTemplates, this.sFDTTemplateResolver),
                    getFetched$(this.store$, getCommissionsFetched, getCommissionDictionary, this.commissionResolver),
                    getFetched$(this.store$, getSurchargeRateFetched, getSurchargeRate, this.surchargeRateResolver),
                    getFetched$(this.store$, getEmployeesFetched, getEmployees, this.employeeResolver),
                ]).pipe(first()),
            ),
            map(([[{ Payload, ARLsForCalculateAmountFromArlIds }, user, partner], customers, offices, partnerOffices, templates, commissions, surchargeRates, employees,]) => {

                const commission = Payload.commissionId ? commissions[Payload.commissionId] : null;
                const automaticInvoiceId = Payload.automaticInvoiceId ? Payload.automaticInvoiceId : false;
                const customer = Payload.customerId ? customers.find((c) => c.Id === Payload.customerId) : null;
                const office = customer ? offices.find((o) => o.CustomerId === customer.Id) : null;

                const ShowNettoSum = true;
                let amount = calculateConsolidatedAmount(ARLsForCalculateAmountFromArlIds, Payload.accountsReceivableLedgerIds || [], [], ShowNettoSum);
                const employee = employees.find((e) => e.UserId === user.Id);

                const invoiceDate = new Date();
                // const deliveryDate = new Date();

                const discountPaymentDate = new Date();
                // deliveryDate.setDate(deliveryDate.getDate() + 30);

                let dueDate: Date | null = new Date();
                if(Payload.documentType !== LedgerImportDocumentTypes.DeliveryNote && !partner?.AdditionalData?.invoiceEditorUseCommissionInvoiceData) {
                    dueDate.setDate(invoiceDate.getDate() + (customer?.PaymentTargetInDays && LedgerImportEntity.IsLedgerImportTypeInvoice(Payload.documentType) ? customer.PaymentTargetInDays : 30));
                }else{
                    dueDate = null;
                }
                discountPaymentDate.setDate(invoiceDate.getDate() + (customer?.PaymentDiscountTargetInDays ? customer.PaymentDiscountTargetInDays : 14));

                let title = '';
                if (partner.PartnerTypeId === PartnerTypeEnum.TSB) {
                    if(Payload.documentType !== LedgerImportDocumentTypes.DeliveryNote && !partner?.AdditionalData?.invoiceEditorUseCommissionInvoiceData) {
                        const newDueDate = tsb.calcLedgerImportDueDate(Payload.documentType, moment(invoiceDate), customer?.PaymentTargetInDays);
                        if (newDueDate) {
                            dueDate = newDueDate.toDate();
                        }
                    }
                    title = tsb.getLedgerImportTitle(title, commission);
                } else if (partner.Id === window["env"]["ibbau_partner_id"] &&  Payload.documentType !== LedgerImportDocumentTypes.DeliveryNote && !partner?.AdditionalData?.invoiceEditorUseCommissionInvoiceData) {
                    const newDueDate = ibbau.calcLedgerImportDueDate(moment(invoiceDate), customer?.PaymentTargetInDays);
                    if (newDueDate) {
                        dueDate = newDueDate.toDate();
                    }
                }
                if (partner?.AdditionalData?.invoiceEditorUseCommissionTitle && commission?.Description){
                    title = commission.Description ;
                }

                const partnerOffice = Payload.partnerOfficeId ? partnerOffices.find((p) => p.Id === Payload.partnerOfficeId) : partnerOffices?.find((p) => p.MainOffice);

                const addData: LedgerImportAdditionalData = {
                    // twigTemplateId: templates.find((t) => t.PartnerId === partner.Id)?.Id || templates.find((t) => t.PartnerId === null)?.Id,
                    LvARLSurchargesByType:[],
                }

                surchargeRates.forEach((scr) => {
                    const arlType = arlTypeForSurchargeRate(scr.Name);
                    if (arlType) {
                        addData.LvARLSurchargesByType.push(
                            {
                                arlType,
                                percentage: scr.PercentageValue,
                            }
                        );
                    }
                });
                var dueDateR = FrontendDate(dueDate);

                return LedgerImportActionTypes.AddLedgerImports({
                    Payload: {

                        hideNetto: ShowNettoSum,
                        title,
                        discountPaymentDate: FrontendDate(discountPaymentDate),
                        accountsReceivableLedgerIds: Payload.accountsReceivableLedgerIds,
                        consolidatedAmount: amount.withAllowanceCharges.bruttoSum,
                        consolidatedDate: FrontendDate(invoiceDate),
                        //consolidatedDeliveryDate: FrontendDate(deliveryDate),
                        //consolidatedDeliveryDate2: FrontendDate(deliveryDate),
                        consolidatedInvoiceId: '',
                        customerId: customer?.Id,
                        commissionId: Payload.commissionId,
                        ustId: partnerOffice?.UstID,
                        iban: partnerOffice?.IBAN,
                        bic: partnerOffice?.Bic,
                        bankname: partnerOffice?.BankName,
                        status: Payload.status || LedgerImportStatusEnum.Neu,
                        commissionNumber: commission?.Auftragsnummer,
                        customerName: customer?.Name,
                        customerEmail: office?.Email,
                        customerPostalCode: office?.PostalCode,
                        customerCity: office?.City,
                        customerStreet: office?.Street,
                        // keine standart Lieferadresse mehr D277-4954
                        // deliveryName: commission?.CustomerName,
                        // deliveryStreet: commission?.Street,
                        // deliveryCity: commission?.City,
                        // deliveryPostalCode: commission?.PostalCode,
                        partnerPhoneNumber: partnerOffice?.PhoneNumber,
                        partnerEmail: partnerOffice?.Email,
                        partnerPostalCode: partnerOffice?.PostalCode,
                        partnerCity: partnerOffice?.City,
                        partnerName: partnerOffice?.Name,
                        partnerStreet: partnerOffice?.Street,
                        partnerCountry: partnerOffice?.Country,
                        userEmail: user.Email,
                        userName: user.DisplayName,
                        userPhoneNumber: employee?.Phonenumber,
                        textBottom:
                        partner.Id === window['env']['luksch_partner_id'] || partner.Id === window['env']['luksch_partner_id2']  ? null :
                            (partner.Id === 117 || partner.Id === 40 || partner.Id === 1) && !(Payload.documentType === 'offer')
                                ? 'Zahlungsbedingungen: \n' + '\n' + 'Zahlbar ohne Abzug innerhalb 14 Tage\t \n' + '\n' + ' \n' + '\n' + 'Leistungszeitraum:     /2021 '
                                : 'Dieses Schreiben wurde maschinell erstellt und ist ohne Unterschrift gültig.',
                        textTop: (partner.Id === 117 || partner.Id === 40 || partner.Id === 1) && !(Payload.documentType === 'offer') ? 'Gutachterliche Beratung  \n' + '\n' + 'BV:  ' : '',
                        documentType: Payload.documentType,
                        automaticInvoiceId: automaticInvoiceId,
                        ledgerImportId: Payload.ledgerImportId,
                        partnerOfficeId: partnerOffice?.Id,
                        dueDate: Payload.documentType === LedgerImportDocumentTypes.DeliveryNote || dueDateR === ''   ? null: dueDateR ,
                        additionalData: JSON.stringify(addData),
                        registryCourt: partnerOffice?.RegistryCourt,
                        commercialCourt: partnerOffice?.CommercialRegister,
                        ceoName: partnerOffice?.CeoName,
                        //Payload.eventId,
                        nebenKostenPercent: partner?.Settings?.defaultNK ? partner.Settings.defaultNK / 100 : undefined,
                        partiestd: partner?.Settings?.defaultPartieStd ? partner.Settings.defaultPartieStd : undefined,
                        eventIds: Payload.eventIds,
                        showLongInformation: true,
                        taxTypeId: customer?.TaxTypeId,
                        showTaxSum: false,
                        partnerAccountHolder: partnerOffice?.AccountHolder,
                    },
                });
            }),
        ),
    );

    AddLedgerImportQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.AddLedgerImports),
            concatMap(({ Payload }) => {
                const status = Payload.status != null ? Payload.status : LedgerImportStatusEnum.Neu;
                const queryString = `mutation { createLedgerImport(
    ${isNotNullOrUndefined(Payload.consolidatedAmount) ? 'consolidatedAmount:' + (Payload.consolidatedAmount * 100).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedInvoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedCurrencyCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedDeliveryDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedDeliveryDate2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedOrderId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commissionId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'textBottom')}
    ${stringifyIfNotNullOrUndefined(Payload, 'textTop')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ustId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'iban')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bic')}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'blz')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankname')}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountsReceivableLedgerIds')}
    status: ${JSON.stringify(status)}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerEmail')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerPostalCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerCity')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerStreet')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerCountry')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerPhoneNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerEmail')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerPostalCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerCity')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerStreet')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerCountry')}
    ${stringifyIfNotNullOrUndefined(Payload, 'userName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'userEmail')}
    ${stringifyIfNotNullOrUndefined(Payload, 'documentType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'rootId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'guaranteedDiscountPercentage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isTemplate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'automaticInvoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'dueDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ledgerImportId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerOfficeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ceoName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'registryCourt')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commercialCourt')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commissionNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'title')}
    ${stringifyIfNotNullOrUndefined(Payload, 'userPhoneNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryTime')}
    ${stringifyIfNotNullOrUndefined(Payload, 'allowanceCharges')}
    ${stringifyIfNotNullOrUndefined(Payload, 'aRLCalculationPreChargeByType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'aRLCalculationGlobalPreCharge')}
    ${stringifyIfNotNullOrUndefined(Payload, 'skontoIsAllowance')}
    ${stringifyIfNotNullOrUndefined(Payload, 'nebenKostenPercent')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryStreet')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryCity')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryPostalCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'servicePeriod')}
    ${stringifyIfNotNullOrUndefined(Payload, 'textBottomFirst')}
    ${stringifyIfNotNullOrUndefined(Payload, 'arlTableStartOnNewPage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showTextBottomFirst')}
    ${stringifyIfNotUndefined(Payload, 'retentionPercentage')}
    ${stringifyIfNotUndefined(Payload, 'warrantyRetentionPercentage')}
    ${stringifyIfNotUndefined(Payload, 'partiestd')}
    ${stringifyIfNotUndefined(Payload, 'eventIds')}
    ${stringifyIfNotUndefined(Payload, 'aRLReadOnlyModeSetByUser')}
    ${stringifyIfNotUndefined(Payload, 'showTaxSum')}
    ${stringifyIfNotUndefined(Payload, 'useDeliveryAddressFromCommission')}
    ${stringifyIfNotUndefined(Payload, 'taxTypeId')}
    ${stringifyIfNotUndefined(Payload, 'showQRCode')}
    ${stringifyIfNotUndefined(Payload, 'partnerAccountHolder')}


    ${Payload.breakPageMode !== undefined ? ('breakPageMode: ' + Payload.breakPageMode) : ''}

    ${Payload.lVKalkulationNebenkosten ? stringifylVKalkulationNebenkosten(Payload.lVKalkulationNebenkosten) : ''}
    ${stringifyIfNotUndefined(Payload, 'lVKalkulationNebenkostenGewerke')}
    ${stringifyIfNotUndefined(Payload, 'isLVCalculated')}
    ${stringifyIfNotUndefined(Payload, 'additionalData')}
    ${stringifyIfNotUndefined(Payload, 'showLongInformation')}
    ${stringifyIfNotUndefined(Payload, 'itemNumbersLocked')}

  ) {${LedgerImportEntity.GqlFields.join(',')}}
        }
        `;
                return this.gatewayHttpService.graphQl({ query: queryString }).pipe(
                    withLatestFrom(this.store$.select(getLedgerImports), this.store$.select(getBusinessVolumeFetched)),
                    tap(([, , bvFetched]) => {
                        if (bvFetched) {
                            this.bvResolver.resolve();
                        }
                    }),
                    concatMap(([res, ledgerImports]) => {
                            if (res && res.createLedgerImport) {
                                this.arlDataService.fetchARlByLedgerImports([res.createLedgerImport.id], true);// ARL neu holen da sich die LedgerImportId geändert haben könnte
                                return [LedgerImportActionTypes.UpdateAll({
                                    Payload: [...ledgerImports, LedgerImportEntityFromBackend(res.createLedgerImport)],
                                }),]
                            } else {
                                return [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Add } })]
                            }
                    }
                    ),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.Add,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    ChangeLedgerImportQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.ChangeLedgerImports),
            concatMap(({ Payload }) => {
                if (!Payload.accountsReceivableLedgerIds) {
                    console.error('no accountsReceivableLedgerIds');
                }
                const hasVars = Payload.html !== undefined;
                const query = `
            mutation${hasVars ? '(' : ''}${Payload.html !== undefined ? '$html: String ' : ''}${hasVars ? ')' : ''} { changeLedgerImport(
    id: ${Payload.id}
    ${isNotNullOrUndefined(Payload.consolidatedAmount) ? 'consolidatedAmount:' + (Payload.consolidatedAmount * 100).toFixed(0) : ''}
    ${isNotNullOrUndefined(Payload.consolidatedNettoAmount) ? 'consolidatedNettoAmount:' + financialRound(Payload.consolidatedNettoAmount * 100, 0) : ''}
    ${stringifyIfNotUndefined(Payload, 'consolidatedDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedInvoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedCurrencyCode')}
    ${stringifyIfNotUndefined(Payload, 'consolidatedDeliveryDate')}
    ${stringifyIfNotUndefined(Payload, 'consolidatedDeliveryDate2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'consolidatedOrderId')}
    ${stringifyIfNotUndefined(Payload, 'customerId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'textBottom')}
    ${stringifyIfNotNullOrUndefined(Payload, 'textTop')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ustId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'iban')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bic')}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'blz')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankname')}
    ${stringifyIfNotNullOrUndefined(Payload, 'booked')}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountsReceivableLedgerIds')}
    ${stringifyIfNotNullOrUndefined(Payload, 'status')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerEmail')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerPostalCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerCity')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerStreet')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerCountry')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerPhoneNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerEmail')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerPostalCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerCity')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerStreet')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerCountry')}
    ${stringifyIfNotNullOrUndefined(Payload, 'userName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'userEmail')}
    ${stringifyIfNotNullOrUndefined(Payload, 'documentType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'rootId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'guaranteedDiscountPercentage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isDirty')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isTemplate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate2')}
    ${stringifyIfNotUndefined(Payload, 'discountPercentage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPercentage2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'automaticInvoiceId')}
    ${stringifyIfNotUndefined(Payload, 'dueDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerOfficeId')}
    ${stringifyIfNotUndefined(Payload, 'commissionId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'additionalData')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ceoName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'registryCourt')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commercialCourt')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commissionNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'hideNetto')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showAmount')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showBaseCost')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showBookingText')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showInformation')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showPosition')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showQuantity')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showQuantityType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showSalaryBaseCost')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showOtherBaseCost')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showTaxSum')}
    ${stringifyIfNotNullOrUndefined(Payload, 'title')}
    ${stringifyIfNotNullOrUndefined(Payload, 'userPhoneNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryTime')}
    ${stringifyIfNotUndefined(Payload, 'taxTypeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'allowanceCharges')}
    ${stringifyIfNotNullOrUndefined(Payload, 'aRLCalculationPreChargeByType')}
    ${stringifyIfNotUndefined(Payload, 'aRLCalculationGlobalPreCharge')}
    ${stringifyIfNotNullOrUndefined(Payload, 'skontoIsAllowance')}
    ${stringifyIfNotUndefined(Payload, 'nebenKostenPercent')}
    ${stringifyIfNotUndefined(Payload, 'employeeArlQuantityTypeId')}
    ${stringifyIfNotUndefined(Payload, 'deliveryName')}
    ${stringifyIfNotUndefined(Payload, 'deliveryStreet')}
    ${stringifyIfNotUndefined(Payload, 'deliveryCity')}
    ${stringifyIfNotUndefined(Payload, 'deliveryPostalCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'servicePeriod')}
    ${stringifyIfNotNullOrUndefined(Payload, 'textBottomFirst')}
    ${stringifyIfNotNullOrUndefined(Payload, 'arlTableStartOnNewPage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showTextBottomFirst')}
    ${stringifyIfNotUndefined(Payload, 'useListPriceForNewMaterials')}
    ${Payload.breakPageMode !== undefined ? ('breakPageMode: ' + Payload.breakPageMode) : ''}
    ${Payload.html != null ? 'html: $html' : ''}
    ${stringifyIfNotUndefined(Payload, 'retentionPercentage')}
    ${stringifyIfNotUndefined(Payload, 'warrantyRetentionPercentage')}
    ${stringifyIfNotUndefined(Payload, 'reviewPeriodText')}
    ${stringifyIfNotUndefined(Payload, 'skontoPeriodText')}
    ${stringifyIfNotUndefined(Payload, 'partiestd')}
    ${Payload.paymentItems ? stringifyPaymentItems(Payload.paymentItems) : ''}

    ${stringifyIfNotUndefined(Payload, 'eventIds')}

    ${Payload.lVKalkulationNebenkosten ? stringifylVKalkulationNebenkosten(Payload.lVKalkulationNebenkosten) : ''}
    ${stringifyIfNotUndefined(Payload, 'lVKalkulationNebenkostenGewerke')}
    ${stringifyIfNotUndefined(Payload, 'isLVCalculated')}
    ${stringifyIfNotUndefined(Payload, 'aRLReadOnlyModeSetByUser')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showLongInformation')}
    ${stringifyIfNotNullOrUndefined(Payload, 'hideLetterHead')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showAmountGross')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showBaseCostGross')}
    ${stringifyIfNotNullOrUndefined(Payload, 'columnsNamesInDocument')}
    ${stringifyIfNotUndefined(Payload, 'useDeliveryAddressFromCommission')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showSalaryBaseCost_cost')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showOtherBaseCost_cost')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showBaseCost_cost')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showBaseCostGross_cost')}
    ${stringifyIfNotUndefined(Payload, 'showQRCode')}
    ${stringifyIfNotUndefined(Payload, 'partnerAccountHolder')}
    ${stringifyIfNotUndefined(Payload, 'itemNumbersLocked')}
    ${stringifyIfNotUndefined(Payload, 'invoiceItemTableStartOnNewPage')}
    ${stringifyIfNotUndefined(Payload, 'hideSumTable')}
    ${stringifyIfNotUndefined(Payload, 'hidePositionsWithoutQuantity')}
  ) {${LedgerImportEntity.GqlFields.join(',')}}
        }
        `;
                const variables = {
                    html: Payload.html,
                };
                const options = {
                    useCompression: useGzip(),
                    retry: true
                };
                return this.gatewayHttpService.graphQl({ query, variables }, options).pipe(
                    withLatestFrom(this.store$.select(getLedgerImports), this.store$.select(getBusinessVolumeFetched), this.store$.select(getPartner)),
                    tap(([res, ledgerImports, bvFetched, partner]) => {
                        if (bvFetched) {
                            this.bvResolver.resolve();
                        }
                        if (res && res.changeLedgerImport) {
                            this.apiToasterService.success('Speichern erfolgreich');
                            if (res.changeLedgerImport.status !== ledgerImports.find((l) => l.Id === res.changeLedgerImport.id)?.Status) {
                                this.apiToasterService.success(`Status auf ${LedgerImportStatusNames.get(res.changeLedgerImport.status)} geändert`);
                                /**
                                 * für D277-4283 angelegt und für D277-5120 wieder auskommentiert
                                 */
                                // if (
                                //     partner.PartnerTypeId === PartnerTypeEnum.TSB
                                //     && res.changeLedgerImport.documentType === LedgerImportDocumentTypes.Offer
                                //     && res.changeLedgerImport.status === LedgerImportStatusEnum.AngebotAkzeptiert
                                // ) {
                                //     this.store$.dispatch(LedgerImportActionTypes.CreateLedgerImportFromLedgerImport({
                                //         Payload: {
                                //             rootId: res.changeLedgerImport.id,
                                //             documentType: LedgerImportDocumentTypes.OfferConfirmation,
                                //         }
                                //     }))
                                //     this.apiToasterService.info(LedgerImportDocumentTypeNames.get(LedgerImportDocumentTypes.OfferConfirmation) + ' wird erstellt');
                                // }
                            }
                        }
                    }),
                    concatMap(([res]) => {
                        if (res && res.changeLedgerImport) {
                            if (res.changeLedgerImport.documentId) {
                                this.fileDataService.GetFileById$(res.changeLedgerImport.documentId, true).pipe(first()).subscribe();
                            }
                            this.arlDataService.fetchARlByLedgerImports([res.changeLedgerImport.id], true) // ARL neu holen da sich die LedgerImportId geändert haben könnte
                            return [
                                LedgerImportActionTypes.UpdateMany({
                                    Payload: [ LedgerImportEntityFromBackend(res.changeLedgerImport)],
                                }),
                                LedgerImportActionTypes.SuccessChangeLedgerImport({
                                    Payload: LedgerImportEntityFromBackend(res.changeLedgerImport),
                                }),
                            ];
                        } else {
                            return [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Modify, Err: {msg: 'wrong response', res} } })];
                        }
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.Modify,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    CreateXRechnungFromLedgerImportQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.CreateXRechnungFromLedgerImport),
            switchMap(({ Payload }) => {
                const query = `mutation{createXRechnungFromLedgerImport(id: ${Payload.id}){${LedgerImportEntity.GqlFields.join(',')}}}`;

                return this.gatewayHttpService.graphQl({ query }).pipe(
                    withLatestFrom(this.store$.select(getLedgerImports), this.store$.select(getBusinessVolumeFetched)),
                    tap(([, , bvFetched]) => {
                        if (bvFetched) {
                            this.bvResolver.resolve();
                        }
                    }),
                    tap(([res, ledgerImports]) => {
                        if (res && res.createXRechnungFromLedgerImport) {
                            this.apiToasterService.success('X-Rechnung generiert');
                        }
                    }),
                    concatMap(([res, ledgerImports]) =>
                        res && res.createXRechnungFromLedgerImport
                            ? [
                                  LedgerImportActionTypes.UpdateAll({
                                      Payload: [...ledgerImports.filter((li) => li.Id !== res.createXRechnungFromLedgerImport.id), LedgerImportEntityFromBackend(res.createXRechnungFromLedgerImport)],
                                  }),
                                  FilesActionTypes.LoadFilesAfter({
                                      Payload: 10,
                                  }), // files neu holen da die Pdf abgelegt worden sein könnte
                                LedgerImportActionTypes.SuccessChangeLedgerImport({
                                    Payload: LedgerImportEntityFromBackend(res.createXRechnungFromLedgerImport),
                                }),
                              ]
                            : [
                                  BaseActionTypes.ErrorAction({
                                      Payload: { ToasterMessage: LedgerImportEffectErrorCodes.CreateXRechnungFromLedgerImport },
                                  }),
                              ],
                    ),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.CreateXRechnungFromLedgerImport,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    DuplicateLedgerImportQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.CreateLedgerImportFromLedgerImport),
            switchMap(({ Payload }) => {
                const query = `
    mutation{
    createLedgerImportFromLedgerImport(
    rootId: ${Payload.rootId}
    ${stringifyIfNotUndefined(Payload, 'customerId')}
    ${stringifyIfNotUndefined(Payload, 'commissionId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isTemplate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'documentType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'templateName')}
    ${stringifyIfNotUndefined(Payload, 'title')}
    ${Payload.arlPosts ? `arlPosts: [${Payload.arlPosts.map((ap) => arlPostQuery(ap)).join(', ')}]` : ''}
    ${stringifyIfNotUndefined(Payload, 'arlIds')}
    ${stringifyIfNotUndefined(Payload, 'itemNumbersLocked')}
  ) {
    ${LedgerImportEntity.GqlFields.join(',')}
    }
  }
        `;
                return this.gatewayHttpService.graphQl({ query }).pipe(
                    withLatestFrom(this.store$.select(getBusinessVolumeFetched)),
                    tap(([, bvFetched]) => {
                        if (bvFetched) {
                            this.bvResolver.resolve();
                        }
                    }),
                    switchMap(([res]) => res && res.createLedgerImportFromLedgerImport ? this.arlDataService.fetchARlByLedgerImports([res.createLedgerImportFromLedgerImport.id], true).then(() => {
                        return res;
                    }) : of(res)),
                    concatMap((res) =>
                        res && res.createLedgerImportFromLedgerImport
                            ? [
                                  LedgerImportActionTypes.CreateLedgerImportFromLedgerImportSuccess({
                                      Payload: LedgerImportEntityFromBackend(res.createLedgerImportFromLedgerImport),
                                  }),
                                  FilesActionTypes.LoadFilesAfter({
                                      Payload: 10,
                                  }), // files neu holen da die Pdf abgelegt worden sein könnte
                              ]
                            : [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Duplicate } })],
                    ),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.Duplicate,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    SfdtTemplateQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.GetSFDTTemplate),
            switchMap(({ Payload, withData }) => {
                const query = `query{ledgerImportTemplate${Payload?.id ? `(id: ${Payload.id})` : ' '}{
    ${SfdtTemplateEntity.GqlFields}
    ${withData ? 'html' : ''}
  }
}
        `;
                return this.gatewayHttpService.graphQl({ query }, { retry: true }).pipe(
                    withLatestFrom(this.store$.select(getSfdtTemplates)),
                    concatMap(([res, templates]) =>
                        res && res.ledgerImportTemplate && (!Payload?.id || res.ledgerImportTemplate.length === 1)
                            ? [
                                  LedgerImportActionTypes.UpdateSFDTTemplate({
                                      // Payload: []
                                      Payload: Payload?.id
                                          ? [...(templates || []).filter((t) => t.Id !== res.ledgerImportTemplate[0].id), SfdtTemplateEntityFromBackend(res.ledgerImportTemplate[0])]
                                          : res.ledgerImportTemplate.map(SfdtTemplateEntityFromBackend),
                                  }),
                                  // BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: 'test' } })
                              ]
                            : [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.sfdtTemplateGet } })],
                    ),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.sfdtTemplateGet,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    DeleteLedgerImportQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.DeleteLedgerImport),
            withLatestFrom(this.store$),
            concatMap(([action, store]) => {
                const query = `
    mutation{
    deleteLedgerImport(
    id: ${action.Payload.id}
  )
  }
        `;
                return this.gatewayHttpService.graphQl({ query }, { token: store.base.token }).pipe(
                    withLatestFrom(this.store$.select(getLedgerImports), this.store$.select(getBusinessVolumeFetched)),
                    tap(([, , bvFetched]) => {
                        if (bvFetched) {
                            this.bvResolver.resolve();
                        }
                    }),
                    concatMap(([res, ledgerImports],) => {
                        if (res /*Todo uncomment && res.deleteLedgerImport*/) {
                            this.arlDataService.fetchARlByLedgerImports([action.Payload.id], true);// ARL neu holen da sich deren status geändert haben könnte
                            return [
                                LedgerImportActionTypes.UpdateAll({
                                    Payload: [...ledgerImports.filter((l) => l.Id !== action.Payload.id)],
                                }),
                            ]
                        } else {
                            return [BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Remove } })]
                        }
                    }
                    ),

                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.Remove,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                );
            }),
        ),
    );
    CreatePartialInvoiceRequestEffect = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.CreatePartialInvoiceRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
mutation{
  createClosingOrPartialInvoiceFromLedgerImport(
    isClosingInvoice: false
    ${stringifyIfNotUndefined(Payload, 'rootId')}
    ${stringifyIfNotUndefined(Payload, 'amount')}
    ${stringifyIfNotUndefined(Payload, 'percentageAmountInformation')}
    ${Payload.arls ? stringifyArls(Payload.arls) : ''}

  ) {
        ${LedgerImportEntity.GqlFields.join(',')}
  }
}
`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: false }).pipe(
                    concatMap((res) => {
                        return res && res.createClosingOrPartialInvoiceFromLedgerImport && res.createClosingOrPartialInvoiceFromLedgerImport.id
                            ? [
                                LedgerImportActionTypes.CreatePartialInvoiceSuccess({ Payload: LedgerImportEntityFromBackend(res.createClosingOrPartialInvoiceFromLedgerImport) }),
                            ]
                            : [LedgerImportActionTypes.CreatePartialInvoiceFailure({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.CreatePartialInvoice, Errors: 'createClosingOrPartialInvoiceFromLedgerImport wrong response' } })];
                    }),
                    catchError((err) => of(LedgerImportActionTypes.CreatePartialInvoiceFailure({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.CreatePartialInvoice, Errors: err } }))),
                );
            }),
        ),
    );
    CreateClosingInvoiceRequestEffect = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.CreateClosingInvoiceRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
mutation{
  createClosingOrPartialInvoiceFromLedgerImport(
    isClosingInvoice: true
    ${stringifyIfNotUndefined(Payload, 'rootId')}
    ${stringifyIfNotUndefined(Payload, 'ledgerImportIds')}
    ${Payload.arls ? stringifyArls(Payload.arls) : ''}
  ) {
        ${LedgerImportEntity.GqlFields.join(',')}
  }
}
`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: false }).pipe(
                    concatMap((res) => {
                        if (res && res.createClosingOrPartialInvoiceFromLedgerImport && res.createClosingOrPartialInvoiceFromLedgerImport.id) {
                            this.arlDataService.fetchARlByLedgerImports([res.createClosingOrPartialInvoiceFromLedgerImport.id], true);
                            return [
                                LedgerImportActionTypes.CreateClosingInvoiceSuccess({ Payload: LedgerImportEntityFromBackend(res.createClosingOrPartialInvoiceFromLedgerImport) }),
                            ] } else {
                            return [LedgerImportActionTypes.CreateClosingInvoiceFailure({
                                Payload: {
                                    ToasterMessage: LedgerImportEffectErrorCodes.CreateClosingInvoice,
                                    Errors: 'createClosingOrPartialInvoiceFromLedgerImport wrong response'
                                }
                            })];
                        }
                    }),
                    catchError((err) => of(LedgerImportActionTypes.CreateClosingInvoiceFailure({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.CreateClosingInvoice, Errors: err } }))),
                );
            }),
        ),
    );

    CreateCumulativePartialInvoiceRequestEffect = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.CreateCumulativePartialInvoiceRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
mutation{
  createClosingOrPartialInvoiceFromLedgerImport(
    isClosingInvoice: false
    ${stringifyIfNotUndefined(Payload, 'rootId')}
    ${stringifyIfNotUndefined(Payload, 'ledgerImportIds')}
    ${stringifyIfNotUndefined(Payload, 'amount')}
    ${stringifyIfNotUndefined(Payload, 'percentageAmountInformation')}
    ${Payload.arls ? stringifyArls(Payload.arls) : ''}
  ) {
        ${LedgerImportEntity.GqlFields.join(',')}
  }
}
`;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: false }).pipe(
                    concatMap((res) => {
                        return res && res.createClosingOrPartialInvoiceFromLedgerImport && res.createClosingOrPartialInvoiceFromLedgerImport.id
                            ? [
                                LedgerImportActionTypes.CreateCumulativePartialInvoiceSuccess({ Payload: LedgerImportEntityFromBackend(res.createClosingOrPartialInvoiceFromLedgerImport) }),
                            ]
                            : [LedgerImportActionTypes.CreateCumulativePartialInvoiceFailure({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.CreateCumulativePartialInvoice, Errors: 'createClosingOrPartialInvoiceFromLedgerImport wrong response' } })];
                    }),
                    catchError((err) => of(LedgerImportActionTypes.CreateCumulativePartialInvoiceFailure({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.CreateCumulativePartialInvoice, Errors: err } }))),
                );
            }),
        ),
    );

    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LedgerImportActionTypes.CreatePartialInvoiceFailure, LedgerImportActionTypes.CreateCumulativePartialInvoiceFailure, LedgerImportActionTypes.CreateClosingInvoiceFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );
}
